/*
 * sl-kaching-price.js — studiolab
 *
 * Mirrors the Kaching Bundles selection onto the theme's OWN price display.
 *
 * THE PROBLEM
 * -----------
 * The Kaching Bundles app renders its own bundle picker and preselects a deal
 * (here: "3 Caixas + 8%OFF"). It rewrites the product form — it sets
 * input[name="quantity"] to the bundle quantity and adds a
 * properties[__kaching_bundles] line property — so the CART is always correct.
 * What it does NOT touch is Palo Alto's price block. The result is two prices
 * on one screen: the bundle bar says R$ 137,73 while the theme still says
 * R$ 49,90, and the add-to-cart button (which also renders
 * [data-product-price]) repeats the stale number a third time.
 *
 * THE HOOK
 * --------
 * The app dispatches a `variants-changed` CustomEvent on its
 * <kaching-bundles-block> element — bubbles: true, composed: true, so a plain
 * document-level listener catches it. Verified payload:
 *
 *   detail = {
 *     variantIdQuantities: [{ variantId: 47434091036821, quantity: 3 }],
 *     freeGifts: [{ id, variantId, quantity }],
 *     upsells: [], bundleProducts: [],
 *     pricing: {
 *       discountedPrice:        { amount: 13773, formatted: "R$ 137,73" },
 *       fullPrice:              { amount: 14970, formatted: "R$ 149,70" },
 *       discountedPricePerItem: { amount:  4591, formatted: "R$ 45,91"  },
 *       fullPricePerItem:       { amount:  4990, formatted: "R$ 49,90"  }
 *     },
 *     formattedPrice: "R$ 137,73",
 *     validation: { requiresItemSelection, allItemsSelected, missingItemsCount }
 *   }
 *
 * Everything needed is in there, already formatted in the shop's money format
 * (R$ {{amount_with_comma_separator}}) AND as integer cents, so the installment
 * line can be recomputed rather than parsed back out of a string.
 *
 * WHAT WE UPDATE
 *   [data-product-price]     -> pricing.discountedPrice     (bundle total)
 *   [data-compare-price]     -> pricing.fullPrice           (shown only if higher)
 *   [data-sl-unit]           -> the existing per-sachê pill, recomputed as
 *                               discountedPricePerItem / count. This is the
 *                               "unit price" already on the page: it was
 *                               server-rendered from the variant price alone, so
 *                               with the 3-box deal selected it claimed
 *                               R$ 4,99 / sachê while the app's own bar label
 *                               beside it said R$ 4,59.
 *   [data-sl-bundle-unit]    -> optional per-box line, pricing.discountedPricePerItem
 *                               + the app's unitLabel ("por caixa"), qty > 1 only
 *   [data-sl-installments]   -> recomputed from discountedPrice.amount
 *
 * WHY NOT THE APP'S OWN SETTING
 * -----------------------------
 * Kaching ships `updateNativePrice` (currently false) with `customSelectors.price`
 * / `.priceCompare` for themes it can't auto-detect. That would cover the price
 * pair, but `updateNativePriceType` is a single choice — bundle total OR per
 * item, not both — and it knows nothing about our sl-installments line. Leave
 * `updateNativePrice` OFF: with both on, two writers fight over the same node.
 *
 * NO-OP BY DESIGN when the app is absent, disabled for the product, or the
 * selection is cleared — in each case the theme's original markup is restored
 * verbatim from the baseline captured at init.
 */
(function () {
  'use strict';

  var PRICE = '[data-product-price]';
  var COMPARE = '[data-compare-price]';
  var UNIT = '[data-sl-bundle-unit]';
  var SACHE = '[data-sl-unit]';
  var INST = '[data-sl-installments]';

  /* Last payload seen, replayed after the theme re-renders the price block on a
     variant change. */
  var lastDetail = null;
  var baseline = null;

  function all(sel) {
    return Array.prototype.slice.call(document.querySelectorAll(sel));
  }

  function money(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      /* The format argument is required — theme.formatMoney() defaults to USD. */
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  /* The app's per-product "unit" wording, e.g. "por caixa". Lives on the block's
     deal-block attribute, so it stays correct per product without a theme
     setting duplicating it. */
  function unitLabel() {
    var block = document.querySelector('kaching-bundles-block');
    if (!block) return '';
    try {
      return (JSON.parse(block.getAttribute('deal-block')).unitLabel || '').trim();
    } catch (e) {
      return '';
    }
  }

  /* ---------------------------------------------------------------------
     Baseline — the theme's own markup, captured before we touch anything so
     a deselect (or a bundle-less variant) restores exactly what Liquid wrote.
     --------------------------------------------------------------------- */
  function capture() {
    baseline = {
      price: all(PRICE).map(function (el) {
        return { el: el, html: el.innerHTML, sale: el.classList.contains('product__price--sale') };
      }),
      compare: all(COMPARE).map(function (el) {
        return { el: el, html: el.innerHTML, display: el.style.display, hidden: el.classList.contains('hidden') };
      }),
      inst: all(INST).map(function (el) {
        return { el: el, html: el.innerHTML };
      }),
      sache: all(SACHE).map(function (el) {
        return { el: el, html: el.innerHTML };
      })
    };
  }

  function restore() {
    if (!baseline) return;
    baseline.price.forEach(function (b) {
      b.el.innerHTML = b.html;
      b.el.classList.toggle('product__price--sale', b.sale);
    });
    baseline.compare.forEach(function (b) {
      b.el.innerHTML = b.html;
      b.el.style.display = b.display;
      b.el.classList.toggle('hidden', b.hidden);
    });
    baseline.inst.forEach(function (b) {
      b.el.innerHTML = b.html;
    });
    baseline.sache.forEach(function (b) {
      b.el.innerHTML = b.html;
    });
    all(UNIT).forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });
  }

  /* ---------------------------------------------------------------------
     Installments — mirrors snippets/sl-installments.liquid exactly. The
     settings ride on the element as data-* attributes so the two never drift.
     Liquid's `divided_by` on integers floors, hence Math.floor here.
     --------------------------------------------------------------------- */
  function installments(el, cents) {
    var max = parseInt(el.getAttribute('data-sl-inst-max'), 10) || 12;
    var minValue = parseFloat(el.getAttribute('data-sl-inst-min')) || 0;
    var prefix = el.getAttribute('data-sl-inst-prefix') || '';
    var juros = el.getAttribute('data-sl-inst-juros') || '';

    var n = max;
    if (minValue > 0) {
      n = Math.min(n, Math.floor(cents / Math.round(minValue * 100)));
    }
    if (n < 2 || cents <= 0) {
      el.innerHTML = '';
      return;
    }
    el.textContent =
      (prefix ? prefix + ' ' : '') +
      n + 'x de ' + money(Math.floor(cents / n)) +
      (juros ? ' ' + juros : '');
  }

  /* ------------------------------------------------------------------- */
  function apply(detail) {
    var pricing = detail && detail.pricing;
    if (!pricing || !pricing.discountedPrice) {
      restore();
      return;
    }

    var qty = (detail.variantIdQuantities || []).reduce(function (sum, v) {
      return sum + (v.quantity || 0);
    }, 0);

    /* Selection cleared (the app allows deselect unless ignoreDeselect is set). */
    if (!qty) {
      restore();
      return;
    }

    var total = pricing.discountedPrice;
    var full = pricing.fullPrice;
    var onSale = !!(full && full.amount > total.amount);

    all(PRICE).forEach(function (el) {
      el.innerHTML = total.formatted;
      el.classList.toggle('product__price--sale', onSale);
    });

    all(COMPARE).forEach(function (el) {
      if (onSale) {
        el.innerHTML = full.formatted;
        el.classList.remove('hidden');
        el.style.display = '';
      } else {
        el.classList.add('hidden');
        el.style.display = 'none';
      }
    });

    var perItem = pricing.discountedPricePerItem;

    /* The per-sachê pill (studiolab "Unit" PDP block). Liquid renders it as
       variant.price / count with integer division; the bundle equivalent is the
       discounted price of ONE box / the same count. Math.floor mirrors Liquid's
       `divided_by` so the JS and SSR values agree to the cent. */
    if (perItem) {
      all(SACHE).forEach(function (el) {
        var count = parseInt(el.getAttribute('data-sl-unit-count'), 10);
        if (!count || count <= 0) return;
        var suffix = el.getAttribute('data-sl-unit-suffix') || '';
        el.textContent = money(Math.floor(perItem.amount / count)) + (suffix ? ' ' + suffix : '');
      });
    }

    /* Optional per-box line. Only meaningful for a multi-item bundle — for
       "1 Caixa" the unit price IS the price, and repeating it reads as a bug. */
    all(UNIT).forEach(function (el) {
      if (qty > 1 && perItem) {
        var label = unitLabel();
        el.textContent = perItem.formatted + (label ? ' ' + label : '');
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });

    all(INST).forEach(function (el) {
      installments(el, total.amount);
    });
  }

  function onVariantsChanged(event) {
    lastDetail = event.detail;
    apply(lastDetail);
  }

  /* ---------------------------------------------------------------------
     Init. The listener is attached before the app's CDN module has loaded
     (this file is a plain deferred script, Kaching's loader is a network-bound
     ES module), so the preselected deal's own variants-changed is caught like
     any other and the first paint is already correct.
     --------------------------------------------------------------------- */
  function init() {
    capture();
    if (lastDetail) apply(lastDetail);
  }

  document.addEventListener('variants-changed', onVariantsChanged);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Palo Alto re-renders the whole price block from a section render on variant
     change, which replaces the nodes we hold references to and reverts them to
     the plain variant price. Re-capture against the new nodes, then replay.
     Kaching re-dispatches on variant change too; applying twice is harmless. */
  function resync() {
    capture();
    if (lastDetail) apply(lastDetail);
  }

  if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
    window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function () {
      setTimeout(resync, 250);
    });
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(resync, 0);
  });
})();
